import type { TargetBox } from "@/components/walkthrough/step-screenshot";
import type {
  Severity,
  FindingType,
  AnalysisLens,
} from "@/components/finding-badges";

export type FindingKind = "all" | "problem" | "strength";
export type TriageStatus =
  | "open"
  | "addressed"
  | "dismissed"
  | "deferred"
  | "wont_fix";

export const toTriageState = (s?: string) =>
  s === "addressed"
    ? "fix_applied"
    : s === "deferred"
      ? "snoozed"
      : s === "wont_fix"
        ? "risk_accepted"
        : s;

export interface FindingDesignCause {
  id?: string;
  code?: string | null;
  title?: string | null;
  severity?: string | null;
  what?: string | null;
  why?: string | null;
  detail?: string | null;
  basis?: string | null;
  screen?: string | null;
  breaksRule?: string | null;
  breaksFile?: string | null;
  description?: string | null;
}

export interface WalkthroughFinding {
  id: string;
  type: FindingType;
  severity: Severity | null;
  triageStatus: TriageStatus;
  fixStatus: string | null;
  lens: AnalysisLens | null;
  title: string | null;
  description: string;
  canonicalFindingId: string | null;
  walkthroughStepId?: string | null;
  targetBox?: TargetBox | null;
  stepNumber?: number | null;
  steps?: number[] | null;
  walkthroughId?: string | null;
  uiFact?: string | null;
  consequence?: string | null;
  what?: string | null;
  why?: string | null;
  basis?: string | null;
  screen?: string | null;
  breaksRule?: string | null;
  breaksFile?: string | null;
  recommendation?: string | null;
  designCauseOf?: string | null;
  designCauses?: FindingDesignCause[];
  trackerLinks?: Array<{
    id: string;
    provider: string;
    kind: string;
    displayKey: string | null;
    url: string | null;
    category: string;
    creationState: string;
  }>;
}

export interface ActiveStepInfo {
  id: string;
  stepNumber: number;
  title: string;
  walkthroughId: string;
  personaReasoning?: string | null;
  pageUrl?: string | null;
}

export interface WorkspaceFindingDrawerProps {
  activeFinding: WalkthroughFinding | null;
  activeStep: ActiveStepInfo | null;
  walkthroughId: string;
  projectId: string;
  canEdit: boolean;
  hasLinkedRepo: boolean;
  isTrackerConfigured: boolean;
  activeProvider: string | null;
  exportConfig: any;
  createIssuePending: boolean;
  onClose: () => void;
  onCreateIssue: (finding: WalkthroughFinding) => void;
  onLinkExisting: (canonicalId: string) => void;
  onViewInTrace: () => void;
  onOpenZen?: () => void;
}

export const kindFilterOptions = [
  { value: "all", label: "All kinds" },
  { value: "problem", label: "Problems only" },
  { value: "strength", label: "Strengths only" },
] as const;

export const severityFilterOptions = [
  { value: "all", label: "All severities" },
  { value: "critical", label: "Critical" },
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" },
  { value: "nit", label: "Nit" },
] as const;
